import { type ReactNode, useEffect } from 'react';
import { type AdminUser, SESSION_KEY, useAuthStore } from './auth-store';

export { SESSION_KEY };

interface PersistedSession {
  token: string;
  user: AdminUser;
}

function loadPersistedSession(): PersistedSession | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PersistedSession>;
    if (parsed?.token && parsed?.user?.id) {
      return { token: parsed.token, user: parsed.user };
    }
  } catch {
    // Corrupt or unavailable storage — fall through to the login screen.
  }
  return null;
}

/**
 * Restores a previously-persisted admin session on mount so a page refresh
 * keeps the user signed in. Authentication itself runs through the embedded
 * passwordless OTP flow in `admin-auth.ts` (engram #737) — no hosted Auth0
 * Universal Login, which ignores `connection=email` on this tenant.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const setAuth = useAuthStore((s) => s.setAuth);
  const setLoading = useAuthStore((s) => s.setLoading);

  useEffect(() => {
    const session = loadPersistedSession();
    if (session) {
      setAuth(session.token, session.user);
    } else {
      setLoading(false);
    }
  }, [setAuth, setLoading]);

  return <>{children}</>;
}
